import React, { useState, useMemo } from 'react'
import { Typography } from '@material-ui/core'
import { Search as SearchIcon } from '@material-ui/icons'
import ServicePicker from './ServicePicker'
import { tokens } from '../../../styles/desertFloraTheme'

const fmt = (v) => '$' + (v || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })

const CATEGORY_ORDER = [
  'General', 'Demo', 'Earthwork', 'Irrigation',
  'Hardscape', 'Planting', 'Lighting', 'Water Feature', 'Closeout'
]

/* ── Inline styles ─────────────────────────────────────────────── */

const sidebarRoot = {
  width: 300, minWidth: 300,
  background: tokens.surface,
  borderRight: `1px solid ${tokens.line}`,
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
  fontFamily: tokens.fontUi
}

const totalCard = {
  margin: '14px 14px 0',
  padding: '14px 16px 12px',
  background: tokens.surface2,
  border: `1px solid ${tokens.line}`,
  borderRadius: 14
}

const eyebrow = {
  fontFamily: tokens.fontMono,
  fontSize: 10.5, fontWeight: 600,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: tokens.ink3,
  margin: 0
}

const totalNumber = {
  fontFamily: tokens.fontMono,
  fontSize: 24, fontWeight: 600,
  color: tokens.ink,
  margin: '4px 0 6px'
}

const neutralPill = {
  display: 'inline-flex', alignItems: 'center',
  fontFamily: tokens.fontMono,
  fontSize: 10.5, fontWeight: 600,
  color: tokens.ink3,
  background: tokens.bg2,
  border: `1px solid ${tokens.line}`,
  borderRadius: 999,
  padding: '2px 8px',
  marginRight: 8
}

const searchRow = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 14px',
  borderBottom: `1px solid ${tokens.line}`
}

const searchInput = {
  flex: 1,
  fontFamily: tokens.fontUi,
  fontSize: 13,
  background: tokens.surface,
  border: `1px solid ${tokens.line}`,
  borderRadius: 8,
  padding: '7px 10px 7px 30px',
  outline: 'none',
  color: tokens.ink,
  transition: 'border-color .12s, box-shadow .12s'
}

const addBtn = {
  fontFamily: tokens.fontUi,
  fontSize: 12, fontWeight: 600,
  background: tokens.primary,
  color: '#fbf8f1',
  border: 'none',
  borderRadius: 10,
  padding: '7px 12px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background .12s'
}

const categoryHeader = {
  fontFamily: tokens.fontMono,
  fontSize: 10.5, fontWeight: 600,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: tokens.ink3,
  padding: '10px 16px 4px',
  margin: 0
}

const assemblyTag = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontFamily: tokens.fontMono,
  fontSize: 10.5, fontWeight: 600,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: '#6e3fb7',
  background: '#ece2fa',
  border: '1px solid currentColor',
  borderRadius: 999,
  padding: '1px 7px 1px 5px',
  marginLeft: 6
}

/* ── Component ─────────────────────────────────────────────────── */

const ServiceSidebar = ({
  projectServices, services, selectedServiceId, onSelectService,
  onServiceAdded, onServiceRemoved, projectId, url
}) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)

  /* ── Build assembly tree grouped by category ── */
  const { grouped, projectTotal, projectCost } = useMemo(() => {
    const all = projectServices || []

    // Build parent lookup
    const parentMap = {} // parentId -> [childPsIds]
    const childSet = new Set()
    for (const ps of all) {
      if (ps.parentService) {
        childSet.add(ps._id)
        const pid = typeof ps.parentService === 'object' ? ps.parentService._id : ps.parentService
        if (!parentMap[pid]) parentMap[pid] = []
        parentMap[pid].push(ps)
      }
      if (ps.childServices?.length) {
        for (const cid of ps.childServices) {
          const childId = typeof cid === 'object' ? cid._id : cid
          childSet.add(childId)
        }
      }
    }

    // Top-level items (not children)
    const topLevel = all.filter(ps => !childSet.has(ps._id))

    // Resolve children for each parent
    const childLookup = {}
    for (const ps of all) {
      if (ps.childServices?.length) {
        childLookup[ps._id] = ps.childServices
          .map(cid => {
            const childId = typeof cid === 'object' ? cid._id || cid : cid
            return all.find(p => p._id === childId || p._id === String(childId))
          })
          .filter(Boolean)
      }
    }

    // Filter by search
    const q = search.toLowerCase()
    const matchesSearch = (ps) => {
      if (!q) return true
      const text = [ps.serviceName, ps.serviceCategory, ps.serviceId].filter(Boolean).join(' ').toLowerCase()
      return text.includes(q)
    }

    // Group by category
    const groups = {}
    for (const ps of topLevel) {
      const children = childLookup[ps._id] || []
      const parentMatches = matchesSearch(ps)
      const anyChildMatches = children.some(c => matchesSearch(c))
      if (!parentMatches && !anyChildMatches) continue

      const cat = ps.serviceCategory || 'Other'
      if (!groups[cat]) groups[cat] = []
      groups[cat].push({
        ...ps,
        _children: children.filter(c => !q || matchesSearch(c) || parentMatches),
        _isAssembly: children.length > 0
      })
    }

    // Sort categories by lifecycle order
    const sorted = {}
    CATEGORY_ORDER.forEach(cat => { if (groups[cat]) sorted[cat] = groups[cat] })
    Object.keys(groups).forEach(cat => { if (!sorted[cat]) sorted[cat] = groups[cat] })

    // Totals (use only top-level to avoid double counting)
    const total = all.filter(ps => !childSet.has(ps._id)).reduce((s, ps) => s + (ps.totalSell || 0), 0)
    const cost = all.filter(ps => !childSet.has(ps._id)).reduce((s, ps) => s + (ps.totalCost || 0), 0)

    return { grouped: sorted, projectTotal: total, projectCost: cost }
  }, [projectServices, search])

  const serviceCount = (projectServices || []).length

  /* ── Render a service row ── */
  const renderRow = (ps, isChild, isAssembly) => {
    const isSelected = ps._id === selectedServiceId
    const hasPrice = (ps.totalSell || 0) > 0
    const areaCount = (ps.areas || []).length

    const rowStyle = {
      padding: isChild ? '6px 14px 6px 32px' : '6px 14px',
      cursor: 'pointer',
      borderLeft: isSelected ? `3px solid ${tokens.primary}` : '3px solid transparent',
      backgroundColor: isSelected
        ? tokens.primarySoft
        : isAssembly
          ? 'rgba(110, 63, 183, 0.03)'
          : 'transparent',
      transition: 'background .1s',
      position: 'relative'
    }

    return (
      <div
        key={ps._id}
        onClick={() => onSelectService(ps._id)}
        style={rowStyle}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = tokens.bg2
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = isAssembly ? 'rgba(110, 63, 183, 0.03)' : 'transparent'
          }
        }}
      >
        {/* Child connector tick */}
        {isChild && (
          <span style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            fontFamily: tokens.fontMono, fontSize: 10, color: tokens.ink4, userSelect: 'none'
          }}>
            —
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{
            fontWeight: isAssembly ? 600 : 500,
            fontSize: 13, color: tokens.ink,
            flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {ps.serviceName}
          </span>
          {isAssembly && (
            <span style={assemblyTag}>
              <span style={{
                width: 5, height: 5, borderRadius: '50%',
                background: 'currentColor', flexShrink: 0
              }} />
              Assembly
            </span>
          )}
        </div>
        <div style={{
          fontFamily: tokens.fontMono,
          fontSize: 11, color: tokens.ink4, marginTop: 2
        }}>
          {areaCount} area{areaCount !== 1 ? 's' : ''}
          {' · '}
          {hasPrice ? (
            <span style={{ color: tokens.ink3, fontWeight: 500 }}>{fmt(ps.totalSell)}</span>
          ) : (
            <span style={{ color: tokens.warn, fontWeight: 500 }}>
              <span style={{ marginRight: 3 }}>&#x2022;</span>
              $0 — needs pricing
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={sidebarRoot}>
      {/* ── Project total card ── */}
      <div style={totalCard}>
        <div style={eyebrow}>Project total · sell</div>
        <div style={totalNumber}>{fmt(projectTotal)}</div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
          <span style={neutralPill}>Cost {fmt(projectCost)}</span>
          <span style={{
            fontFamily: tokens.fontMono, fontSize: 10.5,
            color: tokens.ink4, fontWeight: 500
          }}>
            {serviceCount} service{serviceCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* ── Search + Add row ── */}
      <div style={searchRow}>
        <div style={{ position: 'relative', flex: 1 }}>
          <SearchIcon style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            fontSize: 16, color: tokens.ink4, pointerEvents: 'none'
          }} />
          <input
            type="text"
            placeholder="Search services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              ...searchInput,
              borderColor: searchFocused ? tokens.primary : tokens.line,
              boxShadow: searchFocused ? '0 0 0 3px rgba(31,94,58,.18)' : 'none'
            }}
          />
        </div>
        <button
          style={addBtn}
          onClick={() => setPickerOpen(true)}
          onMouseEnter={(e) => { e.currentTarget.style.background = tokens.primary2 }}
          onMouseLeave={(e) => { e.currentTarget.style.background = tokens.primary }}
        >
          + Add
        </button>
      </div>

      {/* ── Service list — assembly tree ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category}>
            <div style={categoryHeader}>{category}</div>
            {items.map(ps => {
              const isAssembly = ps._isAssembly
              const children = ps._children || []

              // Check if any child is unpriced (for assembly warning)
              const anyChildUnpriced = isAssembly && children.some(c => !(c.totalSell > 0))

              return (
                <React.Fragment key={ps._id}>
                  {renderRow(ps, false, isAssembly)}
                  {isAssembly && anyChildUnpriced && (ps.totalSell || 0) === 0 && null}
                  {children.map(child => renderRow(child, true, false))}
                </React.Fragment>
              )
            })}
          </div>
        ))}

        {serviceCount === 0 && !search && (
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <Typography
              variant="body2"
              style={{ color: tokens.ink4, fontFamily: tokens.fontUi, fontSize: 13 }}
            >
              No services yet. Click "+ Add" to get started.
            </Typography>
          </div>
        )}

        {serviceCount > 0 && Object.keys(grouped).length === 0 && search && (
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <Typography
              variant="body2"
              style={{ color: tokens.ink4, fontFamily: tokens.fontUi, fontSize: 13 }}
            >
              No services match "{search}"
            </Typography>
          </div>
        )}
      </div>

      <ServicePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        services={services}
        projectServices={projectServices}
        projectId={projectId}
        url={url}
        onServiceAdded={onServiceAdded}
        onServiceRemoved={onServiceRemoved}
      />
    </div>
  )
}

export default ServiceSidebar
